<script>
import {
  isInternalUser,
  isUserRoleGreater,
  isUserRoleGreaterOrEqual,
  isUserRoleLess,
  OrgType,
  t,
  toDateTime,
  UserRole,
  userRoleToText
} from '@nova/core';
import { DateTime } from 'luxon';
import config from '@/config/config';
import { RenderModeEnum } from '@/enums';

/**
 * @displayName Navigation Drawer Base
 */
export default {
  name: 'navigation-drawer',
  computed: {
    collapseIconTooltip() {
      return this.isCollapsed
        ? t('navigation.tooltips.expandMenu')
        : t('navigation.tooltips.collapseMenu');
    },
    collapseIcon() {
      return `mdi-arrow-collapse-${this.isCollapsed ? 'right' : 'left'}`;
    },
    navItems() {
      return [
        {
          title: t('navigation.sidebarLinks.appointments', 2),
          link: 'appointments',
          maximumRole: UserRole.OWNER,
          minimumRole: UserRole.SPECTATOR,
          icon: 'mdi-calendar',
          testId: 'appointments-nav-link',
          shouldHide: this.orgRequired
        },
        {
          title: t('navigation.sidebarLinks.searchAppointments'),
          link: 'appointments.search',
          maximumRole: UserRole.OWNER,
          icon: 'mdi-magnify',
          testId: 'search-appointments-nav-link',
          shouldHide: this.orgRequired
        },
        {
          title: t('navigation.sidebarLinks.warehouses', 2),
          link: 'warehouses',
          maximumRole: UserRole.OWNER,
          minimumRole: UserRole.SPECTATOR,
          icon: 'mdi-warehouse',
          testId: 'warehouses-nav-link',
          shouldHide: this.orgRequired
        },
        {
          title: t('navigation.sidebarLinks.gateFeed'),
          link: 'gate-feed',
          maximumRole: UserRole.OWNER,
          minimumRole: UserRole.SPECTATOR,
          icon: 'mdi-cctv mdi-flip-h',
          testId: 'gate-feed-nav-link'
        },
        {
          title: t('navigation.sidebarLinks.yardManagement'),
          link: 'yard',
          maximumRole: UserRole.OWNER,
          minimumRole: UserRole.SPECTATOR,
          icon: 'mdi-view-column-outline',
          testId: 'yard-nav-link'
        },
        {
          title: t('navigation.sidebarLinks.myOrganization'),
          link: 'org',
          maximumRole: UserRole.OWNER,
          minimumRole: UserRole.ADMIN,
          icon: 'mdi-domain',
          testId: 'my-organization-nav-link',
          shouldHide: this.orgRequired
        },
        {
          title: t('navigation.sidebarLinks.users', 2),
          link: 'users',
          maximumRole: UserRole.GOD,
          minimumRole: UserRole.SPECTATOR,
          icon: 'mdi-account-group',
          testId: 'users-nav-link',
          shouldHide: this.orgRequired
        },
        {
          title: t('navigation.sidebarLinks.myCarrierContacts'),
          link: 'carriers',
          maximumRole: UserRole.OWNER,
          minimumRole: UserRole.SPECTATOR,
          icon: 'mdi-truck',
          testId: 'my-carrier-contacts-nav-link',
          shouldHide: this.orgRequired
        },
        {
          title: t('navigation.sidebarLinks.orgs', 2),
          link: 'orgs',
          maximumRole: UserRole.GOD,
          minimumRole: UserRole.INTERNAL,
          icon: 'mdi-lan',
          testId: 'orgs-nav-link'
        },
        {
          title: t('navigation.sidebarLinks.insights'),
          link: 'insights.reporting',
          maximumRole: UserRole.OWNER,
          minimumRole: UserRole.SPECTATOR,
          icon: 'mdi-chart-timeline-variant-shimmer',
          testId: 'insights-nav-link',
          shouldHide: this.orgRequired
        },
        {
          title: t('navigation.sidebarLinks.help'),
          link: 'https://community.loadsmart.com/hc/en-us/sections/24987137080851-New-Warehouse-Portal-Opendock-Nova',
          maximumRole: UserRole.GOD,
          minimumRole: UserRole.BASE,
          icon: 'mdi-help-box',
          external: true,
          testId: 'help-nav-link'
        }
      ];
    },
    isStaging() {
      return config.server_env === 'staging';
    },
    isQA() {
      return config.server_env === 'qa';
    },
    role() {
      return this.$me ? t(`global.roles.${userRoleToText(this.$me.role).toLowerCase()}`) : '';
    },
    navItemsToDisplay() {
      return this.$me ? this.filteredNavItems : [];
    },
    isExpired() {
      return this.trialDaysLeft() <= 0;
    },
    isInternalUser() {
      return isInternalUser(this.$me);
    },
    isTrial() {
      return this.$org.orgType === OrgType.Trial;
    },
    remainingTrialMessage() {
      if (this.isTrial && this.isExpired) {
        return t('navigation.messages.trialExpired');
      } else {
        const trialDaysLeft = this.trialDaysLeft();
        return `${trialDaysLeft} ${t('global.basicTerms.days', trialDaysLeft)}`;
      }
    },
    filteredNavItems() {
      return this.navItems.filter(
        ni =>
          (!ni.minimumRole ||
            (ni.minimumRole && isUserRoleGreaterOrEqual(this.$me.role, ni.minimumRole))) &&
          !ni.shouldHide
      );
    }
  },
  props: {
    /**
     * Determines if certain items are available based on an existing org
     */
    orgRequired: {
      type: Boolean,
      required: true,
      default: true
    },
    unreadMsgCount: {
      type: Number,
      required: false,
      default: 0
    }
  },
  methods: {
    isRouteActive(item) {
      if (!item || !item.link) return false;

      try {
        const resolved = this.$router.resolve({ name: item.link });
        const targetPath = resolved?.path || '';
        const currentPath = this.$route.path;

        // We only want an exact path match like "/org", "/org/settings" so we don't catch similar routes like "/orgs"
        return currentPath === targetPath || currentPath.startsWith(`${targetPath}/`);
      } catch (e) {
        return this.$route.path === item.link || this.$route.path.startsWith(`${item.link}/`);
      }
    },
    toggleCollapse() {
      this.isCollapsed = !this.isCollapsed;
    },
    /**
     * Navigate to specified URL
     * @public
     * @param {string} url
     */
    navigate(url) {
      this.$router.push({ name: url });
    },
    /**
     * Allows for handling of external/internal links dynamically
     * @param {
          title: String,
          link: String,
          minimumRole: String,
          icon: String,
          external: Boolean
        } item
     * @returns {{to: ({name}|null), href: (*|null), target: (string)}}
     */
    getLinkAttrs(item) {
      return {
        to: !item.external ? { name: item.link } : null,
        href: item.external ? item.link : null,
        target: item.external ? '_blank' : ''
      };
    },
    isItemDisabledForUserType(item) {
      if (item.maximumRole && isUserRoleGreater(this.$me.role, item.maximumRole)) {
        return true;
      }

      return Boolean(item.minimumRole && isUserRoleLess(this.$me.role, item.minimumRole));
    },
    isItemDisabled(item) {
      return this.isItemDisabledForUserType(item);
    },
    makeDisabledTooltipMsg(item) {
      if (this.isItemDisabledForUserType(item)) {
        return t('global.tooltips.notAvailableForUserRole');
      }

      return t('global.tooltips.notAvailable');
    },
    handleListItemButtonClick(item) {
      if (item.onClick) {
        this[item.onClick]();
      }
      this.$emit('close');
    },
    trialDaysLeft() {
      return Math.ceil(toDateTime(this.$org.expiresAt).diff(DateTime.now(), 'days').days);
    },
    setRenderMode(renderMode) {
      localStorage.setItem('renderMode', renderMode);
      location.reload();
    }
  },
  data() {
    return {
      config: config,
      drawer: true,
      mini: false,
      neutronVersion: {},
      isCollapsed: false,
      renderModeEnum: RenderModeEnum
    };
  },
  created() {
    if (this.$vuetify.display.smAndDown) {
      this.drawer = false;
    }
    this.$eventHub.$on('navToggle', () => {
      this.drawer = !this.drawer;
    });

    this.$store.dispatch('App/getNeutronVersion');
    this.isCollapsed = JSON.parse(localStorage.getItem('isNavCollapsed')) ?? false;
  },
  watch: {
    isCollapsed(newVal) {
      localStorage.setItem('isNavCollapsed', newVal);
      if (newVal) {
        document.querySelector('body').classList.add('nav-drawer-collapsed');
      } else {
        document.querySelector('body').classList.remove('nav-drawer-collapsed');
      }
    }
  }
};
</script>
